import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  InputAdornment,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { BUSINESS_TYPE_OPTIONS, getBusinessTypeLabel, normalizeHall } from '../utils/normalizeHall';
import { buildHallFormData, MAX_HALL_IMAGES } from '../utils/hallPayload';
import { updateHall } from '../api';
import {
  HALL_DRAWER_FIELD_GAP,
  HALL_FORM_ACCENT,
  getHallDrawerPaperSx,
  getHallFormInputSx,
  getHallFormSectionTitleSx,
} from '../utils/hallDrawerStyles';

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'occupied', label: 'Occupied' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'inactive', label: 'Inactive' },
];

function isObjectUrl(value) {
  return typeof value === 'string' && value.startsWith('blob:');
}

function toEditableHall(hall) {
  const normalized = normalizeHall(hall || {});
  return {
    ...normalized,
    description: normalized.description === 'No description added yet.' ? '' : (normalized.description || ''),
    hall_images: Array.isArray(normalized.hall_images) ? normalized.hall_images : [],
    hall_image_files: [],
    removed_image_ids: [],
    local_image_previews: [],
  };
}

export default function HallEditDrawer({
  open,
  hall,
  isDark = false,
  onClose,
  onSaved,
  onNotice,
}) {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const formInputSx = getHallFormInputSx(isDark);
  const sectionTitleSx = getHallFormSectionTitleSx();
  const drawerPaperSx = getHallDrawerPaperSx(isDark);
  const subtleText = isDark ? '#94a3b8' : '#64748b';
  const textColor = isDark ? '#e2e8f0' : '#0f172a';

  useEffect(() => {
    if (!open) return;
    setDraft(hall ? toEditableHall(hall) : null);
    setSaving(false);
  }, [open, hall]);

  const selectedGalleryCount = useMemo(() => {
    const kept = (draft?.hall_images || []).filter(
      (image) => !(draft?.removed_image_ids || []).includes(image.id),
    ).length;
    const pending = (draft?.hall_image_files || []).length;
    return kept + pending;
  }, [draft]);

  const updateField = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const updatePricing = (field, value) => {
    setDraft((prev) => ({
      ...prev,
      pricing: { ...prev.pricing, [field]: Number(value || 0) },
    }));
  };

  const addImages = (fileList) => {
    const incoming = Array.from(fileList || []).filter((file) => file?.type?.startsWith('image/'));
    if (!incoming.length) return;
    setDraft((prev) => {
      const keptCount = (prev.hall_images || []).filter(
        (image) => !(prev.removed_image_ids || []).includes(image.id),
      ).length;
      const existingFiles = Array.isArray(prev.hall_image_files) ? prev.hall_image_files : [];
      const remainingSlots = Math.max(0, MAX_HALL_IMAGES - (keptCount + existingFiles.length));
      const accepted = incoming.slice(0, remainingSlots);
      if (!accepted.length) {
        onNotice?.('error', `You can upload up to ${MAX_HALL_IMAGES} images per hall.`);
        return prev;
      }
      if (accepted.length < incoming.length) {
        onNotice?.('error', `Only ${remainingSlots} more image(s) can be added (max ${MAX_HALL_IMAGES}).`);
      }
      const localPreviews = accepted.map((file, index) => ({
        id: `local-${Date.now()}-${index}-${file.name}`,
        url: URL.createObjectURL(file),
        name: file.name,
      }));
      return {
        ...prev,
        hall_image_files: [...existingFiles, ...accepted],
        local_image_previews: [...(prev.local_image_previews || []), ...localPreviews],
        thumbnail: prev.thumbnail || localPreviews[0]?.url || '',
      };
    });
  };

  const removeExistingImage = (imageId) => {
    setDraft((prev) => {
      const removed = Array.from(new Set([...(prev.removed_image_ids || []), imageId]));
      const remaining = (prev.hall_images || []).filter((image) => !removed.includes(image.id));
      return {
        ...prev,
        removed_image_ids: removed,
        thumbnail: remaining[0]?.url || prev.local_image_previews?.[0]?.url || '',
      };
    });
  };

  const removeLocalImage = (previewId) => {
    setDraft((prev) => {
      const previews = prev.local_image_previews || [];
      const index = previews.findIndex((item) => item.id === previewId);
      if (index < 0) return prev;
      const target = previews[index];
      if (isObjectUrl(target?.url)) URL.revokeObjectURL(target.url);
      const nextPreviews = previews.filter((_, idx) => idx !== index);
      const nextFiles = (prev.hall_image_files || []).filter((_, idx) => idx !== index);
      const remainingExisting = (prev.hall_images || []).filter(
        (image) => !(prev.removed_image_ids || []).includes(image.id),
      );
      return {
        ...prev,
        local_image_previews: nextPreviews,
        hall_image_files: nextFiles,
        thumbnail: remainingExisting[0]?.url || nextPreviews[0]?.url || '',
      };
    });
  };

  const handleClose = () => {
    if (saving) return;
    (draft?.local_image_previews || []).forEach((item) => {
      if (isObjectUrl(item?.url)) URL.revokeObjectURL(item.url);
    });
    onClose?.();
  };

  const handleSave = async () => {
    if (!draft?.id) return;
    setSaving(true);
    try {
      const formData = buildHallFormData(draft, draft.hall_image_files || []);
      const result = await updateHall(draft.id, formData);
      (draft.local_image_previews || []).forEach((item) => {
        if (isObjectUrl(item?.url)) URL.revokeObjectURL(item.url);
      });
      onNotice?.('success', 'Hall saved successfully');
      onSaved?.(result);
      onClose?.();
    } catch (err) {
      onNotice?.('error', err?.userMessage || err?.message || 'Failed to save hall');
    } finally {
      setSaving(false);
    }
  };

  const renderFormSection = (title, children) => (
    <Stack spacing={1}>
      <Typography sx={sectionTitleSx}>{title}</Typography>
      <Grid
        container
        columnSpacing={HALL_DRAWER_FIELD_GAP}
        rowSpacing={HALL_DRAWER_FIELD_GAP}
        sx={{ width: '100%', maxWidth: '100%', m: 0 }}
      >
        {children}
      </Grid>
      <Divider sx={{ borderColor: isDark ? alpha('#ffffff', 0.08) : alpha('#0f172a', 0.08) }} />
    </Stack>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: drawerPaperSx }}
    >
      {draft ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1.5,
              borderBottom: `1px solid ${isDark ? alpha('#ffffff', 0.1) : alpha('#0f172a', 0.08)}`,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <MapsHomeWorkIcon sx={{ color: HALL_FORM_ACCENT }} />
              <Typography variant="h6" sx={{ fontWeight: 900 }}>Edit Hall</Typography>
            </Stack>
            <Button onClick={handleClose} disabled={saving} sx={{ minWidth: 0, color: isDark ? '#cbd5e1' : '#475569' }}>
              <CloseIcon />
            </Button>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', p: 1.5, boxSizing: 'border-box' }}>
            <Stack spacing={1.5}>
              {renderFormSection('Basic Information', (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Hall Name" value={draft.name} onChange={(e) => updateField('name', e.target.value)} required fullWidth sx={formInputSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Hall Code"
                      value={draft.code || draft.hall_code || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDraft((prev) => ({ ...prev, code: value, hall_code: value }));
                      }}
                      fullWidth
                      sx={formInputSx}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="Description" value={draft.description} onChange={(e) => updateField('description', e.target.value)} fullWidth multiline minRows={2} sx={formInputSx} />
                  </Grid>
                </>
              ))}

              {renderFormSection('Contact Information', (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Manager / Contact Person" value={draft.manager_name} onChange={(e) => updateField('manager_name', e.target.value)} fullWidth sx={formInputSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Mobile Number" value={draft.phone} onChange={(e) => updateField('phone', e.target.value)} fullWidth sx={formInputSx} inputProps={{ inputMode: 'numeric' }} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="Email" value={draft.email} onChange={(e) => updateField('email', e.target.value)} type="email" fullWidth sx={formInputSx} />
                  </Grid>
                </>
              ))}

              {renderFormSection('Business Information', (
                <Grid item xs={12}>
                  <TextField
                    select
                    label="Business Type"
                    value={draft.business_types || []}
                    onChange={(e) => {
                      const next = typeof e.target.value === 'string'
                        ? e.target.value.split(',')
                        : e.target.value;
                      updateField('business_types', next);
                    }}
                    fullWidth
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) => (selected || []).map((value) => getBusinessTypeLabel(value)).join(', '),
                    }}
                    sx={formInputSx}
                  >
                    {BUSINESS_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Checkbox checked={(draft.business_types || []).includes(option.value)} />
                        <ListItemText primary={option.label} />
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              ))}

              {renderFormSection('Capacity & Layout', (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Main Capacity" type="number" value={draft.capacity} onChange={(e) => updateField('capacity', Number(e.target.value || 0))} required fullWidth sx={formInputSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Dining Capacity" type="number" value={draft.dining_capacity} onChange={(e) => updateField('dining_capacity', Number(e.target.value || 0))} fullWidth sx={formInputSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Banquet Style Capacity" type="number" value={draft.banquet_style_capacity} onChange={(e) => updateField('banquet_style_capacity', Number(e.target.value || 0))} fullWidth sx={formInputSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Theatre Style Capacity" type="number" value={draft.theatre_style_capacity} onChange={(e) => updateField('theatre_style_capacity', Number(e.target.value || 0))} fullWidth sx={formInputSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Floor Area" value={draft.floor_area} onChange={(e) => updateField('floor_area', e.target.value)} fullWidth sx={formInputSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Floor / Level" value={draft.floor} onChange={(e) => updateField('floor', e.target.value)} fullWidth sx={formInputSx} />
                  </Grid>
                </>
              ))}

              {renderFormSection('Pricing', (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Hourly Rate" type="number" value={draft.pricing?.hourly_rate ?? ''} onChange={(e) => updatePricing('hourly_rate', e.target.value)} fullWidth InputProps={{ startAdornment: <InputAdornment position="start">INR</InputAdornment> }} sx={formInputSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Full Day Rate" type="number" value={draft.pricing?.full_day_rate ?? ''} onChange={(e) => updatePricing('full_day_rate', e.target.value)} fullWidth InputProps={{ startAdornment: <InputAdornment position="start">INR</InputAdornment> }} sx={formInputSx} />
                  </Grid>
                </>
              ))}

              {renderFormSection('Working Hours', (
                <>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={Boolean(draft.is_24_hours)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setDraft((prev) => ({
                              ...prev,
                              is_24_hours: checked,
                              operating_hours: {
                                open: checked ? '00:00' : (prev.operating_hours?.open || '09:00'),
                                close: checked ? '23:59' : (prev.operating_hours?.close || '23:00'),
                              },
                            }));
                          }}
                          sx={{ color: HALL_FORM_ACCENT, '&.Mui-checked': { color: HALL_FORM_ACCENT } }}
                        />
                      )}
                      label="24 Hours"
                      sx={{ color: textColor, fontWeight: 700 }}
                    />
                  </Grid>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <Grid item xs={12} sm={6}>
                      <TimePicker
                        label="Open Time"
                        ampm
                        format="hh:mm A"
                        disabled={Boolean(draft.is_24_hours)}
                        value={draft.operating_hours?.open ? moment(draft.operating_hours.open, 'HH:mm') : null}
                        onChange={(nextTime) => {
                          setDraft((prev) => ({
                            ...prev,
                            operating_hours: {
                              ...prev.operating_hours,
                              open: nextTime?.isValid?.() ? nextTime.format('HH:mm') : '',
                            },
                          }));
                        }}
                        slotProps={{
                          textField: { fullWidth: true, sx: formInputSx },
                          popper: { disablePortal: true },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TimePicker
                        label="Close Time"
                        ampm
                        format="hh:mm A"
                        disabled={Boolean(draft.is_24_hours)}
                        value={draft.operating_hours?.close ? moment(draft.operating_hours.close, 'HH:mm') : null}
                        onChange={(nextTime) => {
                          setDraft((prev) => ({
                            ...prev,
                            operating_hours: {
                              ...prev.operating_hours,
                              close: nextTime?.isValid?.() ? nextTime.format('HH:mm') : '',
                            },
                          }));
                        }}
                        slotProps={{
                          textField: { fullWidth: true, sx: formInputSx },
                          popper: { disablePortal: true },
                        }}
                      />
                    </Grid>
                  </LocalizationProvider>
                </>
              ))}

              {renderFormSection('Status', (
                <Grid item xs={12}>
                  <TextField select label="Hall Status" value={draft.status || 'available'} onChange={(e) => updateField('status', e.target.value)} fullWidth sx={formInputSx}>
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              ))}

              {renderFormSection('Hall Images', (
                <Grid item xs={12}>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    disabled={selectedGalleryCount >= MAX_HALL_IMAGES}
                    sx={{
                      width: '100%',
                      minHeight: 88,
                      borderStyle: 'dashed',
                      borderColor: alpha(HALL_FORM_ACCENT, 0.5),
                      color: HALL_FORM_ACCENT,
                      fontWeight: 900,
                    }}
                  >
                    {selectedGalleryCount >= MAX_HALL_IMAGES
                      ? `Maximum ${MAX_HALL_IMAGES} images reached`
                      : 'Upload Hall Images'}
                    <input
                      hidden
                      accept="image/*"
                      type="file"
                      multiple
                      onChange={(event) => {
                        addImages(event.target.files);
                        event.target.value = '';
                      }}
                    />
                  </Button>
                  <Typography sx={{ mt: 1, color: subtleText, fontSize: '0.78rem', fontWeight: 700 }}>
                    {selectedGalleryCount}/{MAX_HALL_IMAGES} images · First image is used as the hall cover
                  </Typography>

                  <Box
                    sx={{
                      mt: 1.5,
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(108px, 1fr))',
                      gap: 1,
                    }}
                  >
                    {(draft.hall_images || [])
                      .filter((image) => !(draft.removed_image_ids || []).includes(image.id))
                      .map((image, index) => (
                        <Box
                          key={`existing-${image.id}`}
                          sx={{
                            position: 'relative',
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: `1px solid ${alpha(HALL_FORM_ACCENT, 0.25)}`,
                            aspectRatio: '1 / 1',
                          }}
                        >
                          <Box component="img" src={image.url} alt={`Hall image ${index + 1}`} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          {index === 0 ? (
                            <Chip size="small" label="Cover" sx={{ position: 'absolute', left: 6, top: 6, height: 22, fontWeight: 800, bgcolor: alpha('#031512', 0.72), color: HALL_FORM_ACCENT }} />
                          ) : null}
                          <Button size="small" onClick={() => removeExistingImage(image.id)} sx={{ position: 'absolute', right: 4, top: 4, minWidth: 28, width: 28, height: 28, p: 0, borderRadius: '50%', bgcolor: alpha('#0f172a', 0.72), color: '#fff', '&:hover': { bgcolor: alpha('#fb7185', 0.9) } }}>
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </Button>
                        </Box>
                      ))}

                    {(draft.local_image_previews || []).map((preview, index) => (
                      <Box key={preview.id} sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: `1px dashed ${alpha(HALL_FORM_ACCENT, 0.45)}`, aspectRatio: '1 / 1' }}>
                        <Box component="img" src={preview.url} alt={preview.name || `New hall image ${index + 1}`} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        <Chip size="small" label="New" sx={{ position: 'absolute', left: 6, top: 6, height: 22, fontWeight: 800, bgcolor: alpha('#031512', 0.72), color: HALL_FORM_ACCENT }} />
                        <Button size="small" onClick={() => removeLocalImage(preview.id)} sx={{ position: 'absolute', right: 4, top: 4, minWidth: 28, width: 28, height: 28, p: 0, borderRadius: '50%', bgcolor: alpha('#0f172a', 0.72), color: '#fff', '&:hover': { bgcolor: alpha('#fb7185', 0.9) } }}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </Button>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              ))}
            </Stack>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1,
              p: 1.5,
              borderTop: `1px solid ${isDark ? alpha('#ffffff', 0.1) : alpha('#0f172a', 0.08)}`,
              boxSizing: 'border-box',
            }}
          >
            <Button
              variant="outlined"
              disabled={saving}
              onClick={handleClose}
              sx={{ borderColor: isDark ? alpha('#ffffff', 0.14) : alpha('#0f172a', 0.16), color: isDark ? '#e2e8f0' : '#334155', fontWeight: 900 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !draft.name?.trim() || !Number(draft.capacity || 0)}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              sx={{ bgcolor: HALL_FORM_ACCENT, color: '#031512', fontWeight: 900, '&:hover': { bgcolor: '#3bd1a7' } }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      ) : null}
    </Drawer>
  );
}
